import * as XLSX from "xlsx";
import { getStudentEmergencyRelations, getStudentRelations } from "../../../../modules/common/shared/Util";
import { DuesRules, SHEETS, duesHeader, duesPlanForGrade } from "./studentImportService";

/**
 * Upload checks for the student import file. Every rule here must also be enforced by the backend's
 * validate(row) - the Excel template's own validation is skipped when values are pasted.
 */

export type StudentImportRow = Record<string, any>;

export interface ImportInfo {
  grade: string;
  section: string;
}

export interface ParsedStudentRow {
  rowNumber: number; // row number as shown in Excel
  admissionNo: string;
  name: string;
  data: StudentImportRow;
  dues: Record<string, number>; // academic year name -> amount still owed; blank years are left out
}

export interface RowIssue {
  rowNumber: number;
  admissionNo: string;
  name: string;
  problems: string[];
}

export interface ParseResult {
  importInfo: ImportInfo | null;
  rows: ParsedStudentRow[];
  fileErrors: string[]; // problems with the file as a whole; nothing can be imported
  rowIssues: RowIssue[];
}

const STUDENTS_SHEET = SHEETS.students;
const GRADE_SECTION_SHEET = SHEETS.gradeSection;
const LEGACY_TOTAL_DUES = "Total Dues";

const REQUIRED_FIELDS = [
  "Name *",
  "AdmissionNo *",
  "Gender *",
  "DOB(DD-MMM-YYYY) *",
  "PlaceOfBirth *",
  "Present - House No *",
  "Present - Street Road *",
  "Present - City *",
  "Present - State *",
  "Present - Pincode *",
  "Present - Same As Permanent *",
  "Permanent - House No *",
  "Permanent - Street Road *",
  "Permanent - City *",
  "Permanent - State *",
  "Permanent - Pincode *",
  "Guardian Type *",
  "Guardian - Name *",
  "Guardian - Mobile *",
  "Emergency - Relation *",
  "Emergency - Name *",
  "Emergency - Mobile *",
];

const MOBILE_FIELDS = ["Guardian - Mobile *", "Father - Mobile", "Mother - Mobile", "Emergency - Mobile *"];
const EMAIL_FIELDS = ["Guardian - Email", "Father - Email", "Mother - Email", "Emergency - Email"];
const INCOME_FIELDS = ["Guardian - Monthly Income", "Father - Monthly Income", "Mother - Monthly Income", "Emergency - Monthly Income"];
const PINCODE_FIELDS = ["Present - Pincode *", "Permanent - Pincode *"];

const DOB_FIELD = "DOB(DD-MMM-YYYY) *";
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const text = (value: any): string => (value === undefined || value === null ? "" : value.toString().trim());
const isBlank = (value: any) => text(value) === "";
const formatAmount = (amount: number) => amount.toLocaleString("en-IN");

// Grade & Section sheet: B3 = Grade, B4 = Section (read by cell address, so blank rows above don't shift them)
const readImportInfo = (workbook: XLSX.WorkBook): ImportInfo | null => {
  const sheet = workbook.Sheets[GRADE_SECTION_SHEET];
  if (!sheet) return null;
  return { grade: text(sheet["B3"]?.v), section: text(sheet["B4"]?.v) };
};

// ===================== File-level checks =====================

const checkFile = (
  workbook: XLSX.WorkBook,
  headers: string[],
  importInfo: ImportInfo | null,
  rules: DuesRules
): string[] => {
  const errors: string[] = [];

  if (!importInfo) {
    errors.push(`The '${GRADE_SECTION_SHEET}' sheet is missing. Download the latest template.`);
  } else {
    const grade = rules.orderedGrades.find((g) => g.name === importInfo.grade);
    if (!importInfo.grade) errors.push(`The Grade is missing on the '${GRADE_SECTION_SHEET}' sheet.`);
    else if (!grade) errors.push(`Grade '${importInfo.grade}' on the '${GRADE_SECTION_SHEET}' sheet does not exist.`);

    if (!importInfo.section) errors.push(`The Section is missing on the '${GRADE_SECTION_SHEET}' sheet.`);
    else if (grade && !grade.sections.some((s) => s.name === importInfo.section || s.code === importInfo.section))
      errors.push(`Section '${importInfo.section}' is not a section of Grade ${grade.name}.`);
  }

  // Header row must carry every required column and exactly the system's dues columns
  if (headers.includes(LEGACY_TOTAL_DUES)) {
    errors.push("This file uses the old template (Total Dues column). Download the latest template.");
  }
  const missingRequired = REQUIRED_FIELDS.filter((field) => !headers.includes(field));
  if (missingRequired.length > 0) {
    errors.push(`Columns missing from the Students sheet: ${missingRequired.join(", ")}. Do not rename or remove columns.`);
  }
  // The template is made per grade: it has a dues column only for years where that grade can owe dues
  if (importInfo && rules.limits[importInfo.grade]) {
    const expectedDues = duesPlanForGrade(rules, importInfo.grade).columns.map((c) => duesHeader(c.year));
    const fileDues = headers.filter((h) => h.startsWith("Dues "));
    const missingDues = expectedDues.filter((h) => !fileDues.includes(h));
    const unknownDues = fileDues.filter((h) => !expectedDues.includes(h));
    if (missingDues.length > 0 || unknownDues.length > 0) {
      errors.push(
        `The dues columns don't match the current fee structures for Grade ${importInfo.grade}` +
          (missingDues.length ? ` (missing: ${missingDues.join(", ")})` : "") +
          (unknownDues.length ? ` (not allowed: ${unknownDues.join(", ")})` : "") +
          ". Download the latest template."
      );
    }
  }

  if (!workbook.Sheets[STUDENTS_SHEET]) errors.push(`The '${STUDENTS_SHEET}' sheet is missing. Download the latest template.`);
  return errors;
};

// ===================== Row-level checks =====================

const checkMobile = (row: StudentImportRow, field: string, problems: string[]) => {
  if (isBlank(row[field])) return;
  const digits = text(row[field]).replace(/\D/g, "");
  if (digits.length !== 10) problems.push(`${field} must be a 10 digit mobile number`);
  else if (!/^[789]/.test(digits)) problems.push(`${field} must start with 7, 8 or 9`);
};

const checkDob = (row: StudentImportRow, problems: string[]) => {
  if (isBlank(row[DOB_FIELD])) return;
  const match = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(text(row[DOB_FIELD]));
  if (!match) {
    problems.push("DOB must be in DD-MMM-YYYY format (e.g. 12-May-2010)");
    return;
  }
  const monthIndex = MONTH_NAMES.findIndex((m) => m.toLowerCase() === match[2].toLowerCase());
  if (monthIndex === -1) {
    problems.push("DOB month must be a 3-letter month (Jan, Feb, Mar, ...)");
    return;
  }
  const day = Number(match[1]);
  const year = Number(match[3]);
  const date = new Date(Date.UTC(year, monthIndex, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== monthIndex || date.getUTCDate() !== day) {
    problems.push("DOB is not a valid date");
    return;
  }
  const today = new Date();
  if (date > new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))) {
    problems.push("DOB cannot be a future date");
    return;
  }
  // Normalize casing/padding so the payload always has the same format
  row[DOB_FIELD] = `${String(day).padStart(2, "0")}-${MONTH_NAMES[monthIndex]}-${year}`;
};

const checkGuardian = (row: StudentImportRow, problems: string[]) => {
  const guardianType = text(row["Guardian Type *"]);
  const relationField = "Guardian - Relation *";

  if (guardianType && !["Father", "Mother", "Other"].includes(guardianType)) {
    problems.push("Guardian Type * must be Father, Mother or Other");
    return;
  }

  // Guardian - Relation is filled by an Excel formula for Father/Mother; if the workbook wasn't
  // recalculated it can still be blank, so it is set here as a safety net.
  if ((guardianType === "Father" || guardianType === "Mother") && isBlank(row[relationField])) {
    row[relationField] = guardianType;
  }
  const relation = text(row[relationField]);

  if (guardianType === "Father" || guardianType === "Mother") {
    if (relation !== guardianType) problems.push(`${relationField} must be ${guardianType} when Guardian Type is ${guardianType}`);
  } else if (guardianType === "Other") {
    if (!relation) problems.push(`${relationField} is required when Guardian Type is Other`);
    else if (relation === "Father" || relation === "Mother")
      problems.push(`Guardian Type must be ${relation} when ${relationField} is ${relation}`);
    else if (!getStudentRelations().includes(relation)) problems.push(`${relationField} '${relation}' is not a valid relation`);
  }

  // Both parents' names are needed, except the parent who is the guardian (copied from the guardian)
  const parentGuardian = guardianType === "Father" || guardianType === "Mother" ? guardianType : null;
  if (parentGuardian !== "Father" && isBlank(row["Father - Name *"])) problems.push("Father - Name * is required");
  if (parentGuardian !== "Mother" && isBlank(row["Mother - Name *"])) problems.push("Mother - Name * is required");
};

// For these emergency relations the emergency details are copied (by Excel formula) from that person
const COPIED_EMERGENCY_RELATIONS = ["Father", "Mother", "Guardian"];

const checkEmergency = (row: StudentImportRow, problems: string[]) => {
  const relation = text(row["Emergency - Relation *"]);
  if (!relation) return; // reported as a required field
  if (!getStudentEmergencyRelations().includes(relation)) {
    problems.push(`Emergency - Relation * '${relation}' is not a valid relation`);
    return;
  }
  // Emergency details copied from Father/Mother/Guardian: point at the source columns to fill
  if (COPIED_EMERGENCY_RELATIONS.includes(relation)) {
    const prefix = relation === "Guardian" ? "Guardian" : relation;
    const sourceName = relation === "Guardian" ? "Guardian - Name *" : `${prefix} - Name *`;
    const sourceMobile = relation === "Guardian" ? "Guardian - Mobile *" : `${prefix} - Mobile`;
    if (isBlank(row["Emergency - Name *"])) problems.push(`${sourceName} is required because Emergency - Relation is ${relation}`);
    if (isBlank(row["Emergency - Mobile *"])) problems.push(`${sourceMobile} is required because Emergency - Relation is ${relation}`);
  }
};

const checkDues = (
  row: StudentImportRow,
  importInfo: ImportInfo,
  rules: DuesRules,
  problems: string[]
): Record<string, number> => {
  const dues: Record<string, number> = {};
  const gradeLimits = rules.limits[importInfo.grade];
  const lowestGrade = rules.orderedGrades[0]?.name ?? "the lowest grade";

  rules.duesYears.forEach((year) => {
    const header = duesHeader(year);
    if (isBlank(row[header])) {
      // Every imported student joins the current year, so its dues are required; a blank past year = not in school
      if (year === rules.currentYear) problems.push(`${header} is required (enter 0 if fully paid)`);
      return;
    }

    const raw = text(row[header]).replace(/,/g, "");
    if (!/^\d+$/.test(raw)) {
      problems.push(`${header} must be a whole number (0 or more)`);
      return;
    }
    const amount = Number(raw);
    const limit = gradeLimits?.[year];
    if (!limit) return; // grade itself is invalid - reported as a file error

    if (limit.gradeThen === null) {
      problems.push(`${header}: a ${importInfo.grade} student would have been below ${lowestGrade} in ${year}`);
    } else if (limit.max === null) {
      problems.push(`${header}: no fee set for ${limit.gradeThen} in ${year}. Add it in Finance → Fee Structures`);
    } else if (amount > limit.max) {
      problems.push(`${header} (${formatAmount(amount)}) is more than the ${year} total for ${limit.gradeThen} (${formatAmount(limit.max)})`);
    } else {
      dues[year] = amount;
    }
  });
  return dues;
};

const checkRow = (row: StudentImportRow, importInfo: ImportInfo, rules: DuesRules) => {
  const problems: string[] = [];

  // Copied emergency name/mobile are reported against their source column by checkEmergency
  const emergencyCopied = COPIED_EMERGENCY_RELATIONS.includes(text(row["Emergency - Relation *"]));
  REQUIRED_FIELDS.forEach((field) => {
    if (emergencyCopied && (field === "Emergency - Name *" || field === "Emergency - Mobile *")) return;
    if (isBlank(row[field])) problems.push(`${field} is required`);
  });

  const gender = text(row["Gender *"]);
  if (gender && !["Male", "Female", "Other"].includes(gender)) problems.push("Gender * must be Male, Female or Other");

  const sameAsPermanent = text(row["Present - Same As Permanent *"]);
  if (sameAsPermanent && !["Yes", "No"].includes(sameAsPermanent)) problems.push("Present - Same As Permanent * must be Yes or No");

  PINCODE_FIELDS.forEach((field) => {
    if (!isBlank(row[field]) && !/^\d{6}$/.test(text(row[field]))) problems.push(`${field} must be a 6 digit pincode`);
  });

  MOBILE_FIELDS.forEach((field) => checkMobile(row, field, problems));

  EMAIL_FIELDS.forEach((field) => {
    if (!isBlank(row[field]) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text(row[field]))) problems.push(`${field} is not a valid email`);
  });

  INCOME_FIELDS.forEach((field) => {
    // Whole rupees only: the backend stores monthly income as a whole number (Long)
    if (!isBlank(row[field]) && !/^\d+$/.test(text(row[field]).replace(/,/g, "")))
      problems.push(`${field} must be a whole number (0 or more)`);
  });

  checkDob(row, problems);
  checkGuardian(row, problems);
  checkEmergency(row, problems);
  const dues = checkDues(row, importInfo, rules, problems);

  return { problems, dues };
};

// ===================== Entry point =====================

/** Reads the uploaded workbook and runs every check. Row numbers match the rows shown in Excel. */
export const parseStudentImportFile = (workbook: XLSX.WorkBook, rules: DuesRules): ParseResult => {
  const importInfo = readImportInfo(workbook);
  const sheet = workbook.Sheets[STUDENTS_SHEET];
  const headers: string[] = sheet ? (XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: "" })[0] ?? []).map(text) : [];

  const fileErrors = checkFile(workbook, headers, importInfo, rules);
  if (fileErrors.length > 0 || !sheet || !importInfo) {
    return { importInfo, rows: [], fileErrors, rowIssues: [] };
  }

  const json = XLSX.utils.sheet_to_json<StudentImportRow>(sheet, { defval: "" });
  // Untouched template rows only hold empty formula results, so a row with no value at all is skipped
  const filled = json.filter((row) => Object.values(row).some((value) => !isBlank(value)));
  if (filled.length === 0) {
    return { importInfo, rows: [], fileErrors: ["The Students sheet has no students."], rowIssues: [] };
  }

  const rows: ParsedStudentRow[] = [];
  const rowIssues: RowIssue[] = [];
  const firstRowByAdmissionNo = new Map<string, number>();

  filled.forEach((row) => {
    // sheet_to_json keeps the 0-based sheet row on __rowNum__
    const rowNumber = (row as any).__rowNum__ !== undefined ? (row as any).__rowNum__ + 1 : 0;
    const admissionNo = text(row["AdmissionNo *"]);
    const name = text(row["Name *"]);
    const { problems, dues } = checkRow(row, importInfo, rules);

    if (admissionNo) {
      const key = admissionNo.toLowerCase();
      const firstRow = firstRowByAdmissionNo.get(key);
      if (firstRow !== undefined) problems.push(`AdmissionNo '${admissionNo}' is also used in row ${firstRow}`);
      else firstRowByAdmissionNo.set(key, rowNumber);
    }

    if (problems.length > 0) rowIssues.push({ rowNumber, admissionNo, name, problems });
    else rows.push({ rowNumber, admissionNo, name, data: row, dues });
  });

  return { importInfo, rows, fileErrors: [], rowIssues };
};
